import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildLocationsPageGraph } from '../lib/schema'

const asArray = (value) => (Array.isArray(value) ? value : [])

const Locations = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()
  const location = useLocation()

  const pageTitle = `${t('locations.meta.title')} | VIVA Psychotherapy`
  const pageDescription = t('locations.meta.description')
  const faqItems = asArray(t('locations.faq.items'))
  const nearbyCities = asArray(t('locations.nearby.cities'))
  const counties = asArray(t('locations.counties.items'))
  const allCities = asArray(t('sitemap.sections.serviceAreas.cities'))
  const addressLines = asArray(t('locations.office.addressLines'))

  const contactPath = language === 'pt' ? '/pt/contact' : language === 'en' ? '/en/contact' : '/contact'
  const teamPath = language === 'pt' ? '/pt/team' : language === 'en' ? '/en/team' : '/team'

  usePageSeo({
    enabled: !isLoading,
    title: pageTitle,
    description: pageDescription,
    keywords: t('locations.meta.keywords'),
    pathname: location.pathname
  })

  const schema = useMemo(
    () =>
      buildLocationsPageGraph({
        pathname: location.pathname,
        pageName: pageTitle,
        pageDescription,
        faqItems,
        nearbyCities,
        counties
      }),
    [location.pathname, pageTitle, pageDescription, faqItems, nearbyCities, counties, isLoading]
  )

  useJsonLd('data-locations-schema', schema, !isLoading)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite" data-seo-ready="1">
      <section className="bg-gradient-to-br from-primary-darkGreen to-primary-green py-14 lg:py-20 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">{t('locations.hero.title')}</h1>
          <p className="text-xl text-white/90 mb-3">{t('locations.hero.subtitle')}</p>
          <p className="text-lg text-white/80 max-w-3xl mx-auto">{t('locations.hero.description')}</p>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-primary-darkGreen/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-4">
            {t('locations.answer.heading')}
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">{t('locations.answer.text')}</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-primary-darkGreen mb-6">{t('locations.office.title')}</h2>
            <dl className="space-y-4 text-gray-700">
              <div>
                <dt className="font-semibold text-primary-darkGreen">{t('locations.office.addressLabel')}</dt>
                <dd className="mt-1">
                  <address className="not-italic">
                    {addressLines.map((line) => (
                      <div key={line}>{line}</div>
                    ))}
                  </address>
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-primary-darkGreen">{t('locations.office.hoursLabel')}</dt>
                <dd className="mt-1">{t('locations.office.hours')}</dd>
              </div>
              <div>
                <dt className="font-semibold text-primary-darkGreen">{t('locations.office.phoneLabel')}</dt>
                <dd className="mt-1">
                  <a href={t('locations.office.phoneHref')} className="hover:underline">
                    {t('locations.office.phone')}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-semibold text-primary-darkGreen">{t('locations.office.emailLabel')}</dt>
                <dd className="mt-1">
                  <a href={`mailto:${t('locations.office.email')}`} className="hover:underline break-all">
                    {t('locations.office.email')}
                  </a>
                </dd>
              </div>
            </dl>
            <p className="mt-6 text-sm text-gray-600">{t('locations.office.mapNote')}</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-primary-darkGreen mb-4">{t('locations.telehealth.title')}</h2>
            <p className="text-gray-700 leading-relaxed mb-8">{t('locations.telehealth.text')}</p>
            <h3 className="text-lg font-semibold text-primary-darkGreen mb-3">{t('locations.counties.title')}</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {counties.map((county) => (
                <li key={county}>{county}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-3">
            {t('locations.nearby.title')}
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">{t('locations.nearby.intro')}</p>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {nearbyCities.map((city) => (
              <li
                key={city}
                className="rounded-lg bg-primary-offWhite border border-primary-darkGreen/10 px-4 py-3 text-primary-darkGreen font-medium text-sm sm:text-base"
              >
                {city}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-3">
            {t('locations.allAreas.title')}
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">{t('locations.allAreas.intro')}</p>
          <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-8 text-gray-700 text-sm leading-7">
            {allCities.map((city) => (
              <li key={city} className="break-inside-avoid">
                {city}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 bg-white border-t border-primary-darkGreen/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-darkGreen mb-8">
            {t('locations.faq.title')}
          </h2>
          <div className="space-y-6">
            {faqItems.map((item) => (
              <div key={item.question} className="border-b border-gray-200 pb-6 last:border-0">
                <h3 className="text-lg font-semibold text-primary-darkGreen mb-2">{item.question}</h3>
                <p className="text-gray-700 leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-primary-darkGreen text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">{t('locations.cta.title')}</h2>
          <p className="text-white/90 mb-8 text-lg">{t('locations.cta.text')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={contactPath}
              className="inline-flex items-center rounded-lg bg-white text-primary-darkGreen font-semibold px-6 py-3 hover:bg-primary-offWhite transition-colors"
            >
              {t('locations.cta.contact')}
            </Link>
            <Link
              to={teamPath}
              className="inline-flex items-center rounded-lg border border-white/40 text-white font-semibold px-6 py-3 hover:bg-white/10 transition-colors"
            >
              {t('locations.cta.team')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Locations
